import { useId } from 'react';
import { FileTrigger } from 'react-aria-components';

import { Button } from '../Button/Button.tsx';
import styles from './FileField.module.css';

export interface FileFieldProps {
  /** Always visible, above the control. */
  label: string;
  description?: string;
  /** File types offered by the chooser, e.g. ['.csv', 'text/csv']. */
  acceptedFileTypes?: string[];
  /** The chosen file, shown by name and size; null before one is chosen. */
  file: File | null;
  onChange: (file: File | null) => void;
  /** What is wrong and how to fix it. */
  errorMessage?: string;
  isDisabled?: boolean;
}

/** `12.4 MB`, `820 KB`: sizes as people read them. */
export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${String(Math.round(bytes / 1024))} KB`;
  return `${String(bytes)} bytes`;
}

/**
 * Choose one file from the device, for imports. A button opens the system chooser; the
 * chosen file's name and size are shown in words. The file is read only by the page that
 * uses it; nothing is uploaded until that page does so.
 */
export function FileField({
  label,
  description,
  acceptedFileTypes,
  file,
  onChange,
  errorMessage,
  isDisabled = false,
}: FileFieldProps) {
  const labelId = useId();
  const descriptionId = useId();
  const errorId = useId();
  const describedBy = [
    description === undefined ? null : descriptionId,
    errorMessage === undefined ? null : errorId,
  ]
    .filter((id): id is string => id !== null)
    .join(' ');
  return (
    <div className={styles.field} role="group" aria-labelledby={labelId}>
      <span id={labelId} className={styles.label}>
        {label}
      </span>
      <div className={styles.control}>
        <FileTrigger
          {...(acceptedFileTypes === undefined ? {} : { acceptedFileTypes })}
          onSelect={(list) => {
            onChange(list?.[0] ?? null);
          }}
        >
          <Button
            isDisabled={isDisabled}
            {...(describedBy === '' ? {} : { 'aria-describedby': describedBy })}
          >
            {file === null ? 'Choose file' : 'Choose a different file'}
          </Button>
        </FileTrigger>
        <span className={styles.chosen} aria-live="polite">
          {file === null ? 'No file chosen' : `${file.name}, ${formatFileSize(file.size)}`}
        </span>
      </div>
      {description === undefined ? null : (
        <span id={descriptionId} className={styles.description}>
          {description}
        </span>
      )}
      {errorMessage === undefined ? null : (
        <span id={errorId} className={styles.error}>
          {errorMessage}
        </span>
      )}
    </div>
  );
}
